"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Globe, MapPin, Target, Compass, FileText, ShieldCheck, Clock, ArrowRight, Layers, CircleCheck as CheckCircle2 } from 'lucide-react';
interface ComparisonItem {
  dimension: string;
  icon: React.ElementType;
  national: {
    title: string;
    description: string;
  };
  local: {
    title: string;
    description: string;
  };
}
const comparisonData: ComparisonItem[] = [{
  dimension: "Targeting",
  icon: Target,
  national: {
    title: "Broad Non-Location Keywords",
    description: "Targets commercial and informational queries without geographical intent (e.g., 'B2B SEO strategy' or 'ecommerce migration')."
  },
  local: {
    title: "Geographically Specific Queries",
    description: "Focuses on location-modified searches, 'near me' terms, and city-specific keywords (e.g., 'SEO consultant in Swindon')."
  }
}, {
  dimension: "Geographic Reach",
  icon: Compass,
  national: {
    title: "Nationwide & Multi-Region",
    description: "Establishes visibility across the entire country or multiple national markets within standard organic search engine results."
  },
  local: {
    title: "Targeted City & Radius",
    description: "Dominates specific municipalities, postcodes, or service radii, with top placement in the Google Local 3-Pack and Maps."
  }
}, {
  dimension: "Content Focus",
  icon: FileText,
  national: {
    title: "Deep Topical Hubs & Authority",
    description: "Requires comprehensive topic clusters, buyer-journey content, and thought leadership that addresses broader industry needs."
  },
  local: {
    title: "Service-Area Pages & Citations",
    description: "Prioritises localized service pages, structured NAP consistency, local directory signals, and Google Business Profile optimization."
  }
}, {
  dimension: "Authority Requirements",
  icon: ShieldCheck,
  national: {
    title: "High-Tier Brand & Link Building",
    description: "Demands high domain authority, editorial backlinks, digital PR outreach, and nationwide brand recognition to outrank established competitors."
  },
  local: {
    title: "Localized Signals & Local Links",
    description: "Relies on regional backlinks, community partner mentions, local chamber listings, and genuine customer review signals."
  }
}, {
  dimension: "Realistic Timelines",
  icon: Clock,
  national: {
    title: "6 to 12+ Months",
    description: "Competitive non-branded national terms typically require sustained effort over 6 to 12+ months before seeing top-tier ranking movement."
  },
  local: {
    title: "3 to 6 Months",
    description: "Geographically targeted terms and Local Pack signals often show tangible movement sooner, typically within 3 to 6 months."
  }
}];
export default function NationalVsLocalComparison() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: calloutRef,
    isVisible: calloutVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="nationalvslocalseocomparison" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      {/* Decorative ambient background accents */}
      <div className="pointer-events-none absolute -top-24 -left-24 size-96 rounded-full bg-secondary/40 blur-3xl opacity-60" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 size-96 rounded-full bg-primary/5 blur-3xl opacity-50" aria-hidden="true" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Block */}
        <div ref={headerRef} className={`max-w-3xl transition-all duration-700 transform ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Badge variant="secondary" className="mb-4 bg-secondary text-secondary-foreground font-medium px-3 py-1 rounded-md text-xs uppercase tracking-wider">
            SEO Strategy Comparison
          </Badge>

          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-tight mb-6">
            National SEO vs Local SEO: Which Does Your Business Need?
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-default">
            When you work directly with me, one of our first strategic decisions is determining where your search budget generates the fastest and most sustainable return. Depending on your business model, customer location, and growth goals, the right choice may be National SEO, Local SEO, or a hybrid blend of both.
          </p>
        </div>

        {/* Column Headers for Grid */}
        <div ref={contentRef} className={`mt-12 transition-all duration-700 delay-150 transform ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="hidden lg:grid lg:grid-cols-12 gap-6 mb-6 px-6 py-4 bg-muted/50 rounded-xl border border-border items-center">
            <div className="lg:col-span-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground font-serif">
              Dimension
            </div>
            <div className="lg:col-span-4 flex items-center gap-2">
              <Globe className="size-4 text-primary shrink-0" />
              <span className="font-serif font-semibold text-foreground text-base">
                National SEO
              </span>
            </div>
            <div className="lg:col-span-5 flex items-center gap-2">
              <MapPin className="size-4 text-accent shrink-0" />
              <span className="font-serif font-semibold text-foreground text-base">
                Local SEO
              </span>
            </div>
          </div>

          {/* Detailed Comparison Rows / Cards */}
          <div className="space-y-6 lg:space-y-4">
            {comparisonData.map((item, i) => {
            const IconComponent = item.icon;
            return <Card key={item.dimension} data-index={i} className="border border-border/80 shadow-xs hover:shadow-md transition-all duration-300 bg-card rounded-xl overflow-hidden">
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                      {/* Dimension Title */}
                      <div className="lg:col-span-3 flex items-center gap-3">
                        <div className="p-2.5 rounded-lg bg-secondary/60 text-foreground shrink-0">
                          <IconComponent className="size-5 text-primary" />
                        </div>
                        <div>
                          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground lg:hidden block">
                            Dimension
                          </span>
                          <h3 className="font-serif font-semibold text-foreground text-lg">
                            {item.dimension}
                          </h3>
                        </div>
                      </div>

                      {/* National SEO Column */}
                      <div className="lg:col-span-4 bg-muted/30 lg:bg-transparent p-4 lg:p-0 rounded-lg border border-border/50 lg:border-none">
                        <div className="flex items-center gap-2 mb-2 lg:hidden">
                          <Globe className="size-4 text-primary shrink-0" />
                          <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                            National SEO
                          </span>
                        </div>
                        <h4 className="font-serif text-base font-semibold text-foreground mb-1">
                          {item.national.title}
                        </h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {item.national.description}
                        </p>
                      </div>

                      {/* Local SEO Column */}
                      <div className="lg:col-span-5 bg-secondary/20 lg:bg-transparent p-4 lg:p-0 rounded-lg border border-secondary/40 lg:border-none">
                        <div className="flex items-center gap-2 mb-2 lg:hidden">
                          <MapPin className="size-4 text-accent shrink-0" />
                          <span className="text-xs font-semibold uppercase tracking-wider text-accent-foreground font-semibold">
                            Local SEO
                          </span>
                        </div>
                        <h4 className="font-serif text-base font-semibold text-foreground mb-1">
                          {item.local.title}
                        </h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {item.local.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* Conclusion & Hybrid Approach Callout */}
        <div ref={calloutRef} className={`mt-12 transition-all duration-700 delay-300 transform ${calloutVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Card className="border border-border bg-gradient-to-br from-card via-card to-secondary/30 rounded-2xl shadow-sm p-6 sm:p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="space-y-3 max-w-3xl">
                <div className="flex items-center gap-2 text-primary font-serif font-semibold text-sm uppercase tracking-wider">
                  <Layers className="size-4" />
                  <span>Hybrid SEO Strategy</span>
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground">
                  Need a combination of national authority and local reach?
                </h3>
                <p className="text-muted-foreground text-base leading-relaxed font-default">
                  Many of the growing businesses I consult with do not fit strictly into one box. If you serve clients nationwide while targeting key regional hubs, I design hybrid campaigns that deliver immediate local market capture while systematically building your long-term national authority. You can{" "}
                  <Link to="/LocalSEOPage" className="text-primary font-medium underline underline-offset-4 hover:text-primary/80 transition-colors">
                    read more about my Local SEO services
                  </Link>{" "}
                  to see how I optimize geographically specific assets alongside broader national search targets.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row md:flex-col lg:flex-row gap-3 w-full md:w-auto shrink-0 pt-2 md:pt-0">
                <Button asChild size="lg" className="w-full sm:w-auto">
                  <Link to="/ContactPage">
                    Book a Free SEO Consultation
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                  <Link to="/LocalSEOPage">
                    Explore Local SEO
                  </Link>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>;
}